import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Spin, Button } from "antd";
import PurchaseOrderService from "../../services/purchaseOrder.service";
import DocStatusPill from "./DocStatusPill";

// Task 2194 (F7) -- matches Diamond-PO-Prototype-v3.html's sDash() exactly (lines 853-877):
// request/address insight banners, 5 KPI tiles, inventory position panel.

const fmtMoney = (v) => `$${Number(v || 0).toLocaleString()}`;

const INVENTORY_STATUSES = ["AVAILABLE", "HELD", "MEMO", "RETURNING", "SOLD"];
const LISTED_STATUSES = ["AVAILABLE", "HELD"];

const kpiStyle = (variant) => {
  const variants = {
    default: { bg: "#fff", border: "#eee" },
    red: { bg: "#fdecea", border: "#f5c2bb" },
    amber: { bg: "#fff8ec", border: "#f0dba9" },
  };
  const v = variants[variant] || variants.default;
  return { background: v.bg, border: `1px solid ${v.border}`, borderRadius: 8, padding: 16, flex: "1 1 180px", minWidth: 180 };
};

export const SupplierDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const loginDetials = useSelector((state) => state.loginReducer);
  const dealerId = loginDetials?.loginDetials?.responseData?.dealerId;
  const dealerName = loginDetials?.loginDetials?.responseData?.dealerCompany;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!dealerId) return;
    setLoading(true);
    PurchaseOrderService.GetSupplierDashboard({ DealerID: String(dealerId) })
      .then((r) => {
        setLoading(false);
        if (r.data.status) setData(r.data.responseData);
      })
      .catch(() => setLoading(false));
  }, [dealerId]);

  const inv = INVENTORY_STATUSES.map((status) => {
    const row = (data?.InventoryPosition || []).find((r) => r.AvailabilityStatus === status);
    return {
      status,
      count: row ? row.StoneCount : 0,
      value: row ? row.TotalValue : 0,
    };
  });

  return (
    <div style={{ padding: 20 }}>
      <Spin spinning={loading}>
        <h2 style={{ margin: 0 }}>{dealerName || t("Dashboard")}</h2>
        <p style={{ color: "#888", margin: 0, marginBottom: 16 }}>{t("Consignment exposure, open orders and stones in transit")}</p>

        {data?.RequestsWaiting?.length > 0 && (
          <div style={{ background: "#e8effc", border: "1px solid #c7d6f5", borderRadius: 8, padding: 14, marginBottom: 16 }}>
            <b>
              {data.RequestsWaiting.length} {data.RequestsWaiting.length > 1 ? t("requests waiting for you") : t("request waiting for you")}
            </b>
            <div style={{ marginTop: 4, color: "#444" }}>
              {data.RequestsWaiting.map((r) => `${r.DocNumber} · ${r.RetailerName || "-"} · ${r.DocKind === "PO" ? t("purchase") : t("memo")} · ${r.RequestedStoneCount} ${t("stones")}`).join(" — ")}
            </div>
            <div style={{ marginTop: 8 }}>
              <Button size="small" type="primary" onClick={() => navigate("/requests")}>
                {t("Review")} →
              </Button>
            </div>
          </div>
        )}

        {data && !data.HasReceivingAddress && (
          <div style={{ background: "#fdecea", border: "1px solid #f5c2bb", borderRadius: 8, padding: 14, marginBottom: 16 }}>
            <b>{t("You have no receiving address")}</b>
            <div style={{ marginTop: 4, color: "#444" }}>{t("Retailers holding your stones cannot send them back — returns are blocked.")}</div>
            <div style={{ marginTop: 8 }}>
              <Button size="small" onClick={() => navigate("/addresses")}>
                {t("Add one")}
              </Button>
            </div>
          </div>
        )}

        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
          <div style={kpiStyle("default")}>
            <div style={{ color: "#888", fontSize: 12 }}>{t("Out on memo")}</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{fmtMoney(data?.OutOnMemo?.TotalValue)}</div>
            <div style={{ color: "#888", fontSize: 12 }}>
              {data?.OutOnMemo?.StoneCount || 0} {t("stones · unlisted while out")}
            </div>
          </div>
          <div style={kpiStyle(data?.Overdue?.StoneCount > 0 ? "red" : "default")}>
            <div style={{ color: "#888", fontSize: 12 }}>{t("Overdue")}</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{fmtMoney(data?.Overdue?.TotalValue)}</div>
            <div style={{ color: "#888", fontSize: 12 }}>
              {data?.Overdue?.StoneCount || 0} {t("stones past due")}
            </div>
          </div>
          <div style={kpiStyle("default")}>
            <div style={{ color: "#888", fontSize: 12 }}>{t("Returns in transit")}</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{data?.ReturnsInTransit?.ShipmentCount || 0}</div>
            <div style={{ color: "#888", fontSize: 12 }}>
              {fmtMoney(data?.ReturnsInTransit?.DeclaredValue)} {t("declared")}
            </div>
          </div>
          <div style={kpiStyle("default")}>
            <div style={{ color: "#888", fontSize: 12 }}>{t("Open purchase orders")}</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{fmtMoney(data?.OpenPurchaseOrders?.TotalValue)}</div>
            <div style={{ color: "#888", fontSize: 12 }}>
              {data?.OpenPurchaseOrders?.DocCount || 0} {t("orders")}
            </div>
          </div>
          <div style={kpiStyle("amber")}>
            <div style={{ color: "#888", fontSize: 12 }}>{t("Memo shipping not yet billed")}</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{fmtMoney(data?.MemoShippingUnbilled?.TotalValue)}</div>
            <div style={{ color: "#888", fontSize: 12 }}>{t("billed with the first purchase off each memo")}</div>
          </div>
        </div>

        <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, padding: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
            <h3 style={{ margin: 0 }}>{t("Inventory position")}</h3>
            <span style={{ color: "#888", fontSize: 12 }}>{t("Listed = visible on search, Diamond Link, builders and feeds")}</span>
          </div>
          {inv.map((row) => (
            <div key={row.status} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderTop: "1px solid #f3f3f3" }}>
              <span>
                <DocStatusPill status={row.status} /> <span style={{ color: "#aaa", fontSize: 12 }}>{LISTED_STATUSES.includes(row.status) ? t("listed") : t("not listed")}</span>
              </span>
              <span>
                {row.count} · {fmtMoney(row.value)}
              </span>
            </div>
          ))}
        </div>
      </Spin>
    </div>
  );
};

export default SupplierDashboard;
